import React from 'react';
import {
    Page,
    Text,
    View,
    Document,
    StyleSheet
} from '@react-pdf/renderer';

import '../shared/fonts';
import * as util from '../shared/commons';


const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;

const PAGE_MARGIN = 18;

const CONTENT_WIDTH =
    PAGE_WIDTH - (PAGE_MARGIN * 2);

const STUDENT_ROW_COUNT = 45;

const ROW_HEIGHT = 13;
const HEADER_ROW_HEIGHT = 21;

const SMALL_COLUMN_WIDTH = 22;

const PERSON_COLUMN_WIDTH = 65;

const COMMENT_COLUMN_WIDTH = 122;

const TABLE_WIDTH =
    (SMALL_COLUMN_WIDTH * 11) +
    (PERSON_COLUMN_WIDTH * 3) +
    COMMENT_COLUMN_WIDTH;


if (TABLE_WIDTH !== CONTENT_WIDTH) {
    console.warn(
        'Teacher worksheet table width mismatch:',
        TABLE_WIDTH,
        CONTENT_WIDTH
    );
}


const FONT_FAMILY = 'iransans';


const styles = StyleSheet.create({

    page: {
        width: PAGE_WIDTH,
        height: PAGE_HEIGHT,
        padding: PAGE_MARGIN,
        backgroundColor: '#ffffff',
        fontFamily: FONT_FAMILY,
        direction: 'rtl'
    },

    title: {
        width: '100%',
        height: 20,
        textAlign: 'center',
        fontFamily: FONT_FAMILY,
        fontSize: 10,
        fontWeight: 'bold',
        direction: 'rtl',
        marginBottom: 6
    },

    infoBox: {
        width: '100%',
        height: 50,
        border: '1px solid #000000',
        position: 'relative',
        marginBottom: 8
    },

    schoolName: {
        position: 'absolute',
        top: 5,
        right: 8,
        fontFamily: FONT_FAMILY,
        fontSize: 9,
        textAlign: 'right',
        direction: 'rtl'
    },

    teacherName: {
        position: 'absolute',
        top: 5,
        left: 8,
        width: 230,
        fontFamily: FONT_FAMILY,
        fontSize: 9,
        textAlign: 'right',
        direction: 'rtl',
    },

    className: {
        position: 'absolute',
        bottom: 6,
        right: 8,
        fontFamily: FONT_FAMILY,
        fontSize: 9,
        textAlign: 'right',
        direction: 'rtl'
    },

    courseName: {
        position: 'absolute',
        bottom: 6,
        left: 8,
        width: 230,
        fontFamily: FONT_FAMILY,
        fontSize: 9,
        textAlign: 'right',
        direction: 'rtl',
    },

    table: {
        width: '100%',
        border: '1px solid #000000',
        direction: 'rtl'
    },

    tableRow: {
        width: '100%',
        height: ROW_HEIGHT,
        flexDirection: 'row-reverse',
        direction: 'rtl'
    },

    tableHeaderRow: {
        width: '100%',
        height: HEADER_ROW_HEIGHT,
        flexDirection: 'row-reverse',
        direction: 'rtl',
        backgroundColor: '#e6f0f6'
    },

    cell: {
        height: ROW_HEIGHT,
        borderRight: '1px solid #000000',
        borderBottom: '1px solid #000000',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingLeft: 2,
        paddingRight: 2,
        overflow: 'hidden',
        fontFamily: FONT_FAMILY
    },

    headerCell: {
        height: HEADER_ROW_HEIGHT,
        borderRight: '1px solid #000000',
        borderBottom: '1px solid #000000',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        paddingLeft: 2,
        paddingRight: 2,
        fontFamily: FONT_FAMILY
    },

    headerText: {
        fontFamily: FONT_FAMILY,
        fontSize: 7,
        fontWeight: 'bold',
        textAlign: 'center',
        direction: 'rtl'
    },

    cellText: {
        fontFamily: FONT_FAMILY,
        fontSize: 7,
        textAlign: 'center',
        direction: 'rtl'
    },

    smallCell: {
        width: SMALL_COLUMN_WIDTH,
        fontFamily: FONT_FAMILY
    },

    personCell: {
        width: PERSON_COLUMN_WIDTH,
        fontFamily: FONT_FAMILY
    },

    commentCell: {
        width: COMMENT_COLUMN_WIDTH,
        fontFamily: FONT_FAMILY
    },

    signatureBox: {
        width: '100%',
        height: 50,
        border: '1px solid #000000',
        marginTop: 8,
        position: 'relative',
        direction: 'rtl',
        fontFamily: FONT_FAMILY
    },

    signatureTeacherLabel: {
        position: 'absolute',
        top: 8,
        right: 8,
        fontFamily: FONT_FAMILY,
        fontSize: 9,
        direction: 'rtl',
        textAlign: 'right'
    },

    signatureTeacherValue: {
        position: 'absolute',
        top: 7,
        right: 35,
        fontFamily: FONT_FAMILY,
        fontSize: 9,
        direction: 'rtl',
        textAlign: 'right'
    },

    signatureLine: {
        position: 'absolute',
        top: 8,
        right: 100,
        width: 170,
        borderBottom: '1px dotted #000000',
        height: 12,
        fontFamily: FONT_FAMILY
    },

    signatureLabel: {
        position: 'absolute',
        top: 28,
        right: 8,
        fontFamily: FONT_FAMILY,
        fontSize: 9,
        direction: 'rtl',
        textAlign: 'right'
    },

    signatureLine2: {
        position: 'absolute',
        top: 28,
        right: 100,
        width: 170,
        borderBottom: '1px dotted #000000',
        height: 12
    }

});


function valueOrEmpty(value) {

    if (value === null || value === undefined) {
        return '';
    }

    return String(value);
}


function toPersianDigits(value) {

    if (
        value === null ||
        value === undefined ||
        value === ''
    ) {
        return '';
    }

    return util.toPersianDigits(
        String(value)
    );
}


function getTeacherName(teacher) {

    if (!teacher) {
        return '';
    }

    const firstname =
        valueOrEmpty(
            teacher.firstname
        ).trim();

    const lastname =
        valueOrEmpty(
            teacher.lastname
        ).trim();

    return (
        firstname +
        ' ' +
        lastname
    ).trim();
}


function Cell({
    children,
    type = 'small',
    header = false
}) {

    let cellStyle = styles.smallCell;

    if (type === 'person') {
        cellStyle = styles.personCell;
    }

    if (type === 'comment') {
        cellStyle = styles.commentCell;
    }

    return (
        <View
            style={[
                header
                    ? styles.headerCell
                    : styles.cell,
                cellStyle
            ]}
        >
            <Text
                style={
                    header
                        ? styles.headerText
                        : styles.cellText
                }
            >
                {children}
            </Text>
        </View>
    );
}


function TableHeader() {

    return (
        <View style={styles.tableHeaderRow}>

            <Cell header type="small">
                ردیف
            </Cell>

            <Cell header type="person">
                کد ملی
            </Cell>

            <Cell header type="person">
                نام
            </Cell>

            <Cell header type="person">
                نام خانوادگی
            </Cell>

            <Cell header type="small">
                {toPersianDigits(1)}
            </Cell>

            <Cell header type="small">
                {toPersianDigits(2)}
            </Cell>

            <Cell header type="small">
                {toPersianDigits(3)}
            </Cell>

            <Cell header type="small">
                {toPersianDigits(4)}
            </Cell>

            <Cell header type="small">
                {toPersianDigits(5)}
            </Cell>

            <Cell header type="small">
                {toPersianDigits(6)}
            </Cell>

            <Cell header type="small">
                {toPersianDigits(7)}
            </Cell>

            <Cell header type="small">
                {toPersianDigits(8)}
            </Cell>

            <Cell header type="small">
                {toPersianDigits(9)}
            </Cell>

            <Cell header type="small">
                {toPersianDigits(10)}
            </Cell>

            <Cell header type="comment">
                توضیحات
            </Cell>

        </View>
    );
}


function StudentRow({
    index,
    student
}) {

    student = student || {};

    return (
        <View
            style={styles.tableRow}
            wrap={false}
        >

            <Cell type="small">
                {
                    toPersianDigits(
                        index + 1
                    )
                }
            </Cell>

            <Cell type="person">
                {
                    toPersianDigits(
                        student.nationalId
                    )
                }
            </Cell>

            <Cell type="person">
                {
                    valueOrEmpty(
                        student.firstname
                    )
                }
            </Cell>

            <Cell type="person">
                {
                    valueOrEmpty(
                        student.lastname
                    )
                }
            </Cell>

            <Cell type="small" />
            <Cell type="small" />
            <Cell type="small" />
            <Cell type="small" />
            <Cell type="small" />
            <Cell type="small" />
            <Cell type="small" />
            <Cell type="small" />
            <Cell type="small" />
            <Cell type="small" />

            <Cell type="comment" />

        </View>
    );
}


function StudentTable({
    students
}) {

    const rows = [];

    for (
        let i = 0;
        i < STUDENT_ROW_COUNT;
        i++
    ) {

        const student =
            students &&
            i < students.length
                ? students[i]
                : null;

        rows.push(
            <StudentRow
                key={
                    'student-row-' +
                    i
                }
                index={i}
                student={student}
            />
        );
    }

    return (
        <View style={styles.table}>

            <TableHeader />

            {rows}

        </View>
    );
}


function SheetInfo({
    school,
    teacher,
    sheet
}) {

    const teacherName =
        getTeacherName(teacher);

    const schoolName =
        valueOrEmpty(
            school &&
            school.schoolName
        );

    const className =
        valueOrEmpty(
            sheet &&
            sheet.schoolClass &&
            toPersianDigits(sheet.schoolClass.name)
        );

    const courseName =
        valueOrEmpty(
            sheet &&
            sheet.course &&
            sheet.course.name
        );

    return (
        <View style={styles.infoBox}>

            <Text style={styles.schoolName}>
                مدرسه: {schoolName}
            </Text>

            <Text style={styles.teacherName}>
                معلم: {teacherName}
            </Text>

            <Text style={styles.className}>
                کلاس: {className}
            </Text>

            <Text style={styles.courseName}>
                درس: {courseName}
            </Text>

        </View>
    );
}


function SignatureBox({
    teacher
}) {

    const teacherName =
        getTeacherName(teacher);

    return (
        <View style={styles.signatureBox}>

            <Text
                style={
                    styles.signatureTeacherLabel
                }
            >
                معلم:
            </Text>


            <Text
                style={
                    styles.signatureTeacherValue
                }
            >
                {teacherName}
            </Text>

            <Text
                style={
                    styles.signatureLabel
                }
            >
                امضا:
            </Text>

            <View
                style={
                    styles.signatureLine2
                }
            />

        </View>
    );
}


export default function PrintTeacherSheetDocument(props) {

    const {
        school,
        teacher,
        sheets = []
    } = props;

    return (
        <Document
            title="کاربرگ معلم"
            author="Classico7"
            subject="کاربرگ معلم"
            language="fa-IR"
        >

            {
                sheets.map(
                    (sheet, index) => (

                        <Page
                            key={
                                'teacher-sheet-page-' +
                                (
                                    sheet.schoolClass &&
                                    sheet.schoolClass.id
                                        ? sheet.schoolClass.id
                                        : index
                                )
                            }
                            size="A4"
                            style={styles.page}
                        >

                            <Text style={styles.title}>
                                کاربرگ معلم
                            </Text>

                            <SheetInfo
                                school={school}
                                teacher={teacher}
                                sheet={sheet}
                            />

                            <StudentTable
                                students={
                                    sheet.students || []
                                }
                            />

                            <SignatureBox
                                teacher={teacher}
                            />

                        </Page>

                    )
                )
            }

        </Document>
    );
}
